import { useSelector } from 'react-redux';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import {
  oneErrorOnNotOkFetchye,
  useErrorOnNotOkFetchye,
} from '@americanexpress/fetchye-amex';
import { oneFetchye } from 'fetchye-one-app';
import {
  getCdaasCenturionHomepageUrl,
  getCdaasCulinaryCollectiveUrl,
  getCdaasCenturionLoungeUrl,
} from '@/selectors/cdaasSelectors';
import { getProxyUrl } from '@/selectors/configSelectors';

type AppDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

const culinaryCacheKey = 'cdaas-culinary-collective-content';
const centurionCacheKey = 'cdaas-centurion-homepage-content';
const centurionLoungeCacheKey = 'cdaas-centurion-lounge-content';

const customFetcher = async (
  fetcher: typeof fetch,
  url: string,
  options?: RequestInit
) => {
  let payload;
  let error;
  try {
    const response = await fetcher(url, options);
    payload = {
      body: await response.json(),
      ok: response.ok,
      status: response.status,
    };
  } catch (requestError: unknown) {
    error = requestError;
  }
  return {
    payload,
    error,
  };
};

export const useCdaasCulinaryCollectiveContent = () => {
  const url = useSelector(getCdaasCulinaryCollectiveUrl);
  return useErrorOnNotOkFetchye(
    url,
    {
      mapKeyToCacheKey: () => culinaryCacheKey,
    },
    {
      moduleID: 'travel-lounge-finder-root',
      component: 'useCulinaryCollectiveContent',
    }
  );
};

type LoadCdaasContentOptions = {
  url: string;
  cacheKey: string;
  component: string;
  proxyUrl: string | undefined;
};

const loadCdaasContent = (
  dispatch: AppDispatch,
  { url, cacheKey, component, proxyUrl }: LoadCdaasContentOptions
) => {
  const mapKeyToCacheKey = () => cacheKey;

  if (!global.BROWSER && proxyUrl) {
    const customFetchClient: typeof customFetcher = (
      fetchClient,
      fetchUrl,
      options = {}
    ) =>
      // We need to dynamically import the module
      // so that it is only loaded when it is needed; else
      // https-proxy-agent will break on the browser as it requires tls
      // which is not available on the browser.
      import('https-proxy-agent')
        .then((module) => {
          // this custom fetch client allows us to pass in the agent.
          // this agent is used to make requests through the amex proxy.
          // it relies on the native fetch client.
          // it is not intended to be on the browser.
          const { HttpsProxyAgent } = module;

          return customFetcher(fetchClient, fetchUrl, {
            ...options,
            agent: new HttpsProxyAgent(proxyUrl),
          } as RequestInit & { agent: unknown });
        })
        // regular fetch client if the dynamic import fails to create the proxy agent
        .catch(() => customFetcher(fetch, fetchUrl, options));

    return dispatch(
      // @ts-expect-error -- fetchye-one-app oneFetchye type does not have the 3rd argument for some reason
      oneFetchye(url, { mapKeyToCacheKey }, customFetchClient)
    );
  }

  return dispatch(
    oneErrorOnNotOkFetchye(
      url,
      { mapKeyToCacheKey },
      { moduleID: 'travel-lounge-finder-root', component }
    )
  );
};

export const loadCdaasCulinaryCollectiveContent = (
  dispatch: AppDispatch,
  getState: () => unknown
) =>
  loadCdaasContent(dispatch, {
    url: getCdaasCulinaryCollectiveUrl(getState()),
    cacheKey: culinaryCacheKey,
    component: 'loadCulinaryCollectiveContent',
    proxyUrl: getProxyUrl(getState()),
  });

export const useCdaasCenturionHomepageContent = () => {
  const url = useSelector(getCdaasCenturionHomepageUrl);
  return useErrorOnNotOkFetchye(
    url,
    {
      mapKeyToCacheKey: () => centurionCacheKey,
    },
    {
      moduleID: 'travel-lounge-finder-root',
      component: 'useCenturionHomepageContent',
    }
  );
};

export const loadCdaasCenturionHomepageContent = (
  dispatch: AppDispatch,
  getState: () => unknown
) =>
  loadCdaasContent(dispatch, {
    url: getCdaasCenturionHomepageUrl(getState()),
    cacheKey: centurionCacheKey,
    component: 'loadCenturionHomepageContent',
    proxyUrl: getProxyUrl(getState()),
  });

export const loadCdaasCenturionLoungeContent = (
  dispatch: AppDispatch,
  getState: () => unknown
) =>
  loadCdaasContent(dispatch, {
    url: getCdaasCenturionLoungeUrl(getState()),
    cacheKey: centurionLoungeCacheKey,
    component: 'loadCenturionLoungeContent',
    proxyUrl: getProxyUrl(getState()),
  });

export const useCdaasCenturionLoungeContent = () => {
  const url = useSelector(getCdaasCenturionLoungeUrl);
  return useErrorOnNotOkFetchye(
    url,
    {
      mapKeyToCacheKey: () => centurionLoungeCacheKey,
      defer: !global.BROWSER,
    },
    {
      moduleID: 'travel-lounge-finder-root',
      component: 'useCenturionLoungeContent',
    }
  );
};
